import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useClinicPortalServiceType } from '@/hooks/main/clinicPortalServiceType';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import {
  getMassagerLocations,
  type ClinicLocationOption,
  type MassagerOption,
} from '@/services/main/treatmentReport';
import type { TreatmentReportFormMode } from '../types';
import { formatClinicLocationLabel, formatMassagerOptionLabel } from '../utils';

interface UseTreatmentReportMassagerLocationsParams {
  control: Control<TreatmentReportFormData>;
  watch: UseFormWatch<TreatmentReportFormData>;
  setValue: UseFormSetValue<TreatmentReportFormData>;
  formMode: TreatmentReportFormMode;
  massagers: MassagerOption[];
  disabled: boolean;
  patientId?: string;
  initialMassager?: { id: number; name: string | null; serial_number?: number | null } | null;
  initialClinicLocation?: ClinicLocationOption | null;
}

export const useTreatmentReportMassagerLocations = ({
  control,
  watch,
  setValue,
  formMode,
  massagers,
  disabled,
  patientId,
  initialMassager = null,
  initialClinicLocation = null,
}: UseTreatmentReportMassagerLocationsParams) => {
  const { tenant, clinicNumber } = useClinicContext();
  const svcType = useClinicPortalServiceType();
  const [clinicLocations, setClinicLocations] = useState<ClinicLocationOption[]>([]);
  const watchedYm = useWatch({ control, name: 'treatment_yearmonth' });
  const watchedMassagerId = useWatch({ control, name: 'massager_id' });
  const watchedClinicLocationId = useWatch({ control, name: 'clinic_location_id' });
  const {
    errors: { clinic_location_id: clinicLocationError },
    isSubmitted,
  } = useFormState({ control });
  const showClinicLocationError = isSubmitted && !!clinicLocationError;
  const shouldApplyMassagerLocationRuleRef = useRef(false);

  useEffect(() => {
    if (!watchedMassagerId || !patientId || !tenant || !clinicNumber) {
      setClinicLocations([]);
      if (formMode !== 'edit') {
        setValue('clinic_location_id', undefined as any, { shouldDirty: false });
      }
      return;
    }

    const shouldApplyMassagerLocationRule = shouldApplyMassagerLocationRuleRef.current;
    const currentLocationId = watch('clinic_location_id');

    setClinicLocations([]);
    let cancelled = false;
    getMassagerLocations(tenant, clinicNumber, patientId, svcType, watchedMassagerId)
      .then(res => {
        if (cancelled) return;
        const locs = res.data?.locations ?? [];
        setClinicLocations(locs);
        if (!shouldApplyMassagerLocationRule) return;

        const nextLocationId = locs.length === 1 ? locs[0].id : undefined;
        if (currentLocationId !== nextLocationId) {
          setValue('clinic_location_id', nextLocationId as any, { shouldDirty: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setClinicLocations([]);
        if (shouldApplyMassagerLocationRule && currentLocationId !== undefined) {
          setValue('clinic_location_id', undefined as any, { shouldDirty: true });
        }
      })
      .finally(() => {
        if (!cancelled && shouldApplyMassagerLocationRule) {
          shouldApplyMassagerLocationRuleRef.current = false;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [watchedMassagerId, patientId, tenant, clinicNumber, svcType, setValue, formMode, watch]);

  const locationOptions = useMemo(() => {
    const options = clinicLocations.map(l => ({
      value: l.id,
      label: formatClinicLocationLabel(l),
    }));

    if (
      initialClinicLocation &&
      watchedClinicLocationId === initialClinicLocation.id &&
      !options.some(o => o.value === initialClinicLocation.id)
    ) {
      options.unshift({
        value: initialClinicLocation.id,
        label: formatClinicLocationLabel(initialClinicLocation),
      });
    }

    return options;
  }, [clinicLocations, watchedClinicLocationId, initialClinicLocation]);

  const massagerOptions = useMemo(() => {
    if (!watchedYm) {
      return initialMassager && watchedMassagerId === initialMassager.id
        ? [
            {
              value: initialMassager.id,
              label: formatMassagerOptionLabel(initialMassager),
            },
          ]
        : [];
    }

    const options = [...massagers]
      .sort((a, b) => (a.serial_number ?? 0) - (b.serial_number ?? 0))
      .map(m => ({
        value: m.id,
        label: `${m.serial_number ?? ''}：${m.name}`,
      }));

    if (
      initialMassager &&
      watchedMassagerId === initialMassager.id &&
      !options.some(o => o.value === initialMassager.id)
    ) {
      options.unshift({
        value: initialMassager.id,
        label: formatMassagerOptionLabel(initialMassager),
      });
    }

    return options;
  }, [massagers, watchedYm, watchedMassagerId, initialMassager]);

  useEffect(() => {
    if (disabled) return;
    if (!watchedYm) {
      if (formMode === 'edit') return;
      setValue('massager_id', undefined as any, { shouldDirty: true });
      setValue('clinic_location_id', undefined as any, { shouldDirty: true });
      setClinicLocations([]);
      return;
    }
    if (!watchedMassagerId) return;
    if (massagers.length === 0 && !initialMassager) return;
    const stillValid = massagerOptions.some(o => o.value === watchedMassagerId);
    if (!stillValid) {
      setValue('massager_id', undefined as any, { shouldDirty: true });
      setValue('clinic_location_id', undefined as any, { shouldDirty: true });
      setClinicLocations([]);
    }
  }, [
    disabled,
    formMode,
    watchedYm,
    watchedMassagerId,
    massagers.length,
    initialMassager,
    massagerOptions,
    setValue,
  ]);

  const handleMassagerChange = useCallback(
    (value: number | undefined, onChange: (value: number | undefined) => void) => {
      shouldApplyMassagerLocationRuleRef.current = true;
      onChange(value);
      setValue('clinic_location_id', undefined as any, { shouldDirty: true });
      setClinicLocations([]);
    },
    [setValue]
  );

  const handleClinicLocationChange = useCallback(
    (value: number | null | undefined) => {
      setValue('clinic_location_id', (value == null ? undefined : Number(value)) as any, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const handleFillMassagerInfo = useCallback(() => {
    const location =
      clinicLocations.find(l => l.id === watchedClinicLocationId) ??
      (initialClinicLocation?.id === watchedClinicLocationId ? initialClinicLocation : undefined);

    if (location) {
      setValue('location_name', location.location_name ?? '');
      setValue('postal_code1', location.postal_code1 ?? '');
      setValue('postal_code2', location.postal_code2 ?? '');
      setValue('address', [location.address1, location.address2].filter(Boolean).join('') || '');
      setValue('telephone', location.telephone ?? '');
      setValue('fax', location.fax ?? '');

      const selectedId = watch('massager_id');
      const massager = massagers.find(m => m.id === selectedId);
      setValue('email', massager?.email ?? '');
      return;
    }

    const selectedId = watch('massager_id');
    if (!selectedId) {
      setValue('location_name', '');
      setValue('postal_code1', '');
      setValue('postal_code2', '');
      setValue('address', '');
      setValue('telephone', '');
      setValue('fax', '');
      setValue('email', '');
    }
  }, [clinicLocations, watchedClinicLocationId, initialClinicLocation, massagers, watch, setValue]);

  return {
    clinicLocationError,
    handleClinicLocationChange,
    handleFillMassagerInfo,
    handleMassagerChange,
    locationOptions,
    massagerOptions,
    showClinicLocationError,
    watchedClinicLocationId,
    watchedMassagerId,
    watchedYm,
  };
};

